/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { RefObject, useRef, FC, PropsWithChildren } from 'react'
import NextImage from 'next/image'
import { Gallery, GalleryProps, Item } from 'react-photoswipe-gallery'
import { analytics, analyticsPostHogOmit } from '@helpers/analytics'
import { resizeShopifyImage } from '@helpers/shopify'
import Carousel, { CarouselSlide, CarouselType } from '@components/ui/Carousel'
import classNames from 'classnames'
import 'photoswipe/dist/photoswipe.css'
import 'photoswipe/dist/default-skin/default-skin.css'
import productGridStyles from './productGrid.module.scss'
import styles from './carousel.module.scss'
import photoswipeStyles from './photoswipe.module.scss'
import { ProductGalleryImage } from './types'

const PhotoswipeGallery = Gallery as FC<PropsWithChildren<GalleryProps>>

type ProductImagesCarouselProps = {
  images: ProductGalleryImage[]
  productName: string
}

function ProductImagesCarousel({
  images,
  productName
}: ProductImagesCarouselProps) {
  const carouselRef = useRef<CarouselType>()

  const carouselSettings = {
    loop: true,
    dots: true,
    slidesToScroll: 1,
    startIndex: 0,
    afterChange: () => {
      analytics(
        'Carousel',
        {
          label: 'Carousel swiped'
        },
        {
          integrations: {
            PostHog: false
          }
        }
      )
    },
    buttons: false
  }

  const productImagesStyles = classNames(
    styles.productImages__list,
    productGridStyles.productGridPhotoSwipe
  )

  const imagesLoaded = (imageIndex: number) => {
    analyticsPostHogOmit('Gallery interaction', {
      action: 'Gallery opened',
      label: `Image (${imageIndex})`
    })
    analytics('Click', {
      category: 'Product Page',
      label: `Image (${imageIndex})`
    })
  }

  return (
    <div className={`${productImagesStyles} ${photoswipeStyles.photoswipe}`}>
      <PhotoswipeGallery
        options={{
          captionEl: false,
          showHideOpacity: true,
          maxSpreadZoom: 4,
          closeOnScroll: false,
          preload: [1, 3],
          history: false,
          barsSize: { top: 0, bottom: 0 },
          loadingIndicatorDelay: 500,
          preloaderEl: true,
          counterEl: false
        }}
        onOpen={photoswipe => {
          imagesLoaded(photoswipe.getCurrentIndex())

          photoswipe.listen('close', () => {
            analyticsPostHogOmit('Gallery interaction', {
              category: 'Product Page',
              action: 'Gallery closed'
            })
          })

          photoswipe.listen('beforeChange', (direction: number) => {
            analyticsPostHogOmit('Gallery interaction', {
              action: 'Carousel swipe',
              name: 'Carousel',
              label: `Carousel ${
                direction === 1 ? 'next' : 'previous'
              } ${photoswipe.getCurrentIndex()}`,
              context: 'default'
            })
          })
        }}
      >
        <Carousel
          settings={carouselSettings}
          instance={carouselInstance => {
            carouselRef.current = carouselInstance
          }}
          carouselDotsClasses={styles.carouselDots}
        >
          {images.map((image, imageIndex) => (
            <CarouselSlide
              key={imageIndex}
              classes={styles.productImages__imageWrapper}
            >
              <Item original={image?.src} width={1200} height={1200}>
                {({ ref, open }) => (
                  <NextImage
                    ref={ref as RefObject<HTMLImageElement>}
                    onKeyUp={open}
                    onClick={open}
                    src={resizeShopifyImage(image?.src, { width: '600' })}
                    width={600}
                    height={600}
                    alt={image?.alt || productName}
                  />
                )}
              </Item>
            </CarouselSlide>
          ))}
        </Carousel>
      </PhotoswipeGallery>
    </div>
  )
}

export default ProductImagesCarousel
